/**
 * Occupation standards — one place for the citations and the group names.
 *
 * The registry classifies every role against NCO-2015 (India) and ISCO-08
 * (ILO). Both the note component, the methodology page, the FAQs and the
 * per-listing metadata read their labels and links from here so the claim
 * never drifts between screens.
 *
 * Display only. Nothing here feeds scoring.
 */

export const OCCUPATION_STANDARDS_SENTENCE =
  "Careers are classified against NCO-2015 and ISCO-08, the official Indian and international occupation standards.";

export interface StandardSource {
  id: "nco2015" | "isco08";
  code: string;
  name: string;
  custodian: string;
  href: string;
  hrefLabel: string;
  summary: string;
}

export const OCCUPATION_STANDARD_SOURCES: StandardSource[] = [
  {
    id: "nco2015",
    code: "NCO-2015",
    name: "National Classification of Occupations 2015",
    custodian: "Ministry of Labour & Employment, Government of India",
    href: "https://www.dget.nic.in/content/innerpage/national-classification-of-occupation.php",
    hrefLabel: "dget.nic.in — National Classification of Occupations",
    summary:
      "India's official occupation code list. It is what makes a role recognisable to an Indian school board, university or employer.",
  },
  {
    id: "isco08",
    code: "ISCO-08",
    name: "International Standard Classification of Occupations 2008",
    custodian: "International Labour Organization (ILO)",
    href: "https://ilostat.ilo.org/methods/concepts-and-definitions/classification-occupation/",
    hrefLabel: "ilostat.ilo.org — Classification of occupation",
    summary:
      "The global standard, organised into 10 major groups. It is what lets the same role be compared across countries.",
  },
];

/** ISCO-08 major groups, 0–9. Used to name the basis applied to a listing. */
export const ISCO08_MAJOR_GROUPS: Record<string, string> = {
  "0": "Armed forces occupations",
  "1": "Managers",
  "2": "Professionals",
  "3": "Technicians and associate professionals",
  "4": "Clerical support workers",
  "5": "Services and sales workers",
  "6": "Skilled agricultural, forestry and fishery workers",
  "7": "Craft and related trades workers",
  "8": "Plant and machine operators, and assemblers",
  "9": "Elementary occupations",
};

/** ILO skill levels 1–4, in words a person can act on. */
export const ISCO08_SKILL_LEVELS: Record<number, { short: string; meaning: string }> = {
  1: {
    short: "Skill level 1",
    meaning: "Simple, routine tasks. Primary education, or on-the-job training, is usually enough.",
  },
  2: {
    short: "Skill level 2",
    meaning:
      "Secondary education plus vocational training or experience with the tools of the trade.",
  },
  3: {
    short: "Skill level 3",
    meaning: "A diploma or technical qualification beyond school — typically one to three years.",
  },
  4: {
    short: "Skill level 4",
    meaning: "A degree or higher, with complex problem-solving and decision-making expected.",
  },
};

/** How a role got its precise code, and how much weight that carries. */
export const ISCO08_MATCH_METHODS: Record<
  string,
  { label: string; meaning: string; precise: boolean }
> = {
  exact_title: {
    label: "Exact title match",
    meaning: "The role title matches an ILO occupation title exactly.",
    precise: true,
  },
  alias_title: {
    label: "Alternative title match",
    meaning: "The role matches one of the alternative job titles the ILO lists for this group.",
    precise: true,
  },
  fuzzy_title: {
    label: "Similar title match",
    meaning:
      "The closest ILO title, matched on similarity. Close enough to place the role, so we label it rather than present it as exact.",
    precise: true,
  },
  cohort_derived: {
    label: "Matched through its career family",
    meaning:
      "Placed in the same official group as the rest of its career family. A good guide to where this role sits, and we say so rather than implying a direct match.",
    precise: true,
  },
  group_inherited: {
    label: "Major group only",
    meaning:
      "We hold the broad family this role belongs to, but not a four-digit unit group yet. Shown as a family rather than a code we cannot stand behind.",
    precise: false,
  },
};

export interface ClassificationBasis {
  /** Short chip text, e.g. "ISCO-08 · 2511" or "NCO-2015 / ISCO-08 · 2". */
  label: string;
  /** Long form for a title/aria description. */
  description: string;
  /** True when the row carries an actual ISCO-08 major group. */
  resolved: boolean;
  /** True when the row carries a four-digit unit group. */
  precise: boolean;
}

export type OccupationCodingRow = {
  isco08_major_group?: string | null;
  isco08_group_name?: string | null;
  isco08_sub_major_group?: string | null;
  isco08_minor_group?: string | null;
  isco08_unit_group?: string | null;
  isco08_skill_level?: number | null;
  isco08_match_method?: string | null;
  isco08_match_confidence?: number | null;
};

/**
 * Describe which classification basis was applied to one listing.
 *
 * A four-digit unit group is the precise answer, so it leads when we hold one.
 * With only a major group we say the family and nothing more. With neither we
 * say so plainly rather than implying a code we do not hold.
 */
export function classificationBasis(row: OccupationCodingRow): ClassificationBasis {
  const unit = (row.isco08_unit_group ?? "").toString().trim();
  const raw = (row.isco08_major_group ?? "").toString().trim();

  if (unit) {
    const method = ISCO08_MATCH_METHODS[row.isco08_match_method ?? ""];
    const level = row.isco08_skill_level ? ISCO08_SKILL_LEVELS[row.isco08_skill_level] : null;
    return {
      label: `ISCO-08 · ${unit}`,
      description: `Classified against NCO-2015 and ISCO-08. ISCO-08 unit group ${unit}${
        level ? `, ${level.short.toLowerCase()}` : ""
      }.${method ? ` ${method.label}.` : ""}`,
      resolved: true,
      precise: true,
    };
  }

  if (!raw) {
    return {
      label: "Classification pending",
      description:
        "No ISCO-08 major group is recorded for this role yet, so it is shown without a classification basis rather than with a guessed one.",
      resolved: false,
      precise: false,
    };
  }

  const major = raw.charAt(0);
  const groupName = row.isco08_group_name?.trim() || ISCO08_MAJOR_GROUPS[major] || null;
  return {
    label: `NCO-2015 / ISCO-08 · ${raw}`,
    description: `Classified against NCO-2015 and ISCO-08. ISCO-08 major group ${raw}${
      groupName ? `: ${groupName}` : ""
    }.`,
    resolved: true,
    precise: false,
  };
}

/** "Exact title match · high confidence" style sentence for the detail panel. */
export function confidencePhrase(confidence: number | null | undefined): string | null {
  if (confidence == null || Number.isNaN(confidence)) return null;
  if (confidence >= 0.9) return "high confidence";
  if (confidence >= 0.75) return "good confidence";
  return "indicative confidence";
}

